@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

@tailwind base;
@tailwind components;
@tailwind utilities;

/* ------------------------------------------------------------------
   SGSCTIC — Charte graphique
   Marine #092653 · Orange industriel #F47721 · Gris métallique #87939F
   Blanc #FFFFFF · Anthracite #17191D
------------------------------------------------------------------ */
:root {
  --font-heading: 'Barlow Condensed', 'Arial Narrow', sans-serif;
  --font-body: 'Inter', 'Helvetica Neue', sans-serif;
  --font-label: 'Inter', 'Helvetica Neue', sans-serif;

  /* Papier chaud / gris acier clair — la base n'est plus un blanc pur */
  --background-50: 0.973 0.008 84;
  --background-100: 0.944 0.013 82;
  --background-200: 0.9 0.017 80;
  --background-300: 0.842 0.02 78;
  --background-400: 0.752 0.022 76;
  --background-500: 0.658 0.022 74;
  --background-600: 0.558 0.021 72;
  --background-700: 0.455 0.019 70;
  --background-800: 0.365 0.017 68;
  --background-900: 0.282 0.015 66;
  --background-950: 0.198 0.013 64;

  /* Textes : anthracite #17191D */
  --foreground-50: 0.978 0.002 255;
  --foreground-100: 0.945 0.003 255;
  --foreground-200: 0.888 0.004 255;
  --foreground-300: 0.8 0.005 255;
  --foreground-400: 0.688 0.006 255;
  --foreground-500: 0.578 0.007 255;
  --foreground-600: 0.49 0.008 255;
  --foreground-700: 0.4 0.008 256;
  --foreground-800: 0.322 0.008 258;
  --foreground-900: 0.262 0.008 260;
  --foreground-950: 0.215 0.008 262;

  /* Marine profond #092653 */
  --primary-50: 0.972 0.012 258;
  --primary-100: 0.936 0.024 258;
  --primary-200: 0.878 0.042 259;
  --primary-300: 0.792 0.058 260;
  --primary-400: 0.632 0.099 261;
  --primary-500: 0.335 0.088 262;
  --primary-600: 0.283 0.078 262;
  --primary-700: 0.234 0.066 262;
  --primary-800: 0.198 0.056 262;
  --primary-900: 0.163 0.047 262;
  --primary-950: 0.124 0.038 262;

  /* Orange industriel #F47721 */
  --accent-50: 0.974 0.018 55;
  --accent-100: 0.94 0.04 55;
  --accent-200: 0.89 0.072 52;
  --accent-300: 0.822 0.112 49;
  --accent-400: 0.748 0.152 47;
  --accent-500: 0.685 0.178 45;
  --accent-600: 0.602 0.162 43;
  --accent-700: 0.512 0.14 41;
  --accent-800: 0.424 0.116 39;
  --accent-900: 0.342 0.092 37;
  --accent-950: 0.234 0.062 36;

  /* Gris métallique #87939F */
  --secondary-50: 0.974 0.004 245;
  --secondary-100: 0.94 0.006 245;
  --secondary-200: 0.89 0.009 245;
  --secondary-300: 0.8 0.012 245;
  --secondary-400: 0.718 0.015 245;
  --secondary-500: 0.665 0.017 245;
  --secondary-600: 0.575 0.016 245;
  --secondary-700: 0.48 0.014 245;
  --secondary-800: 0.385 0.012 245;
  --secondary-900: 0.29 0.01 245;
  --secondary-950: 0.2 0.008 245;
}

html {
  scroll-behavior: smooth;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

body {
  font-family: var(--font-body);
  background-color: oklch(var(--background-50));
  color: oklch(var(--foreground-950));
}

::selection {
  background-color: oklch(var(--primary-500));
  color: oklch(var(--background-50));
}

/* ---------- Scrollbar ---------- */
::-webkit-scrollbar {
  width: 10px;
}
::-webkit-scrollbar-track {
  background: oklch(var(--background-100));
}
::-webkit-scrollbar-thumb {
  background: oklch(var(--secondary-400));
  border-radius: 999px;
}
::-webkit-scrollbar-thumb:hover {
  background: oklch(var(--primary-500));
}

@layer components {
  .container-wide {
    width: 100%;
    max-width: 1320px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
  @media (min-width: 768px) {
    .container-wide {
      padding-left: 2rem;
      padding-right: 2rem;
    }
  }

  .label-mono {
    font-family: var(--font-label);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
  }

  .display-title {
    font-family: var(--font-heading);
    font-weight: 600;
    line-height: 0.94;
    letter-spacing: -0.005em;
    text-transform: uppercase;
  }

  /* Trame technique diagonale — texture de marque discrète */
  .hatch-lines {
    background-image: repeating-linear-gradient(
      135deg,
      oklch(var(--foreground-950) / 0.06) 0px,
      oklch(var(--foreground-950) / 0.06) 1px,
      transparent 1px,
      transparent 8px
    );
  }

  /* Trame papier / plan — donne de la matière aux sections claires */
  .paper-grid {
    background-image:
      linear-gradient(oklch(var(--foreground-950) / 0.045) 1px, transparent 1px),
      linear-gradient(90deg, oklch(var(--foreground-950) / 0.045) 1px, transparent 1px);
    background-size: 40px 40px;
  }

  .paper-grid-dark {
    background-image:
      linear-gradient(oklch(var(--background-50) / 0.07) 1px, transparent 1px),
      linear-gradient(90deg, oklch(var(--background-50) / 0.07) 1px, transparent 1px);
    background-size: 48px 48px;
  }

  .band-rule {
    background-image: linear-gradient(
      90deg,
      oklch(var(--accent-500)) 0%,
      oklch(var(--accent-500)) 72px,
      oklch(var(--foreground-950) / 0.12) 72px,
      oklch(var(--foreground-950) / 0.12) 100%
    );
    height: 2px;
  }

  .hatch-lines-light {
    background-image: repeating-linear-gradient(
      135deg,
      oklch(var(--background-50) / 0.12) 0px,
      oklch(var(--background-50) / 0.12) 1px,
      transparent 1px,
      transparent 9px
    );
  }

  /* Grille de plan technique — utilisée sur les supports de marque */
  .blueprint-grid {
    background-image:
      linear-gradient(oklch(var(--primary-500) / 0.1) 1px, transparent 1px),
      linear-gradient(90deg, oklch(var(--primary-500) / 0.1) 1px, transparent 1px);
    background-size: 22px 22px;
  }

  .link-underline {
    position: relative;
  }
  .link-underline::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -4px;
    height: 2px;
    width: 100%;
    background: currentColor;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .link-underline:hover::after,
  .link-underline[data-active='true']::after {
    transform: scaleX(1);
  }
}

/* ---------- Reveal on scroll ---------- */
.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition:
    opacity 800ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 800ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}
.reveal[data-visible='true'] {
  opacity: 1;
  transform: translateY(0);
}
.reveal-fade {
  opacity: 0;
  transition: opacity 1000ms ease;
}
.reveal-fade[data-visible='true'] {
  opacity: 1;
}
.reveal-clip {
  clip-path: inset(0 0 100% 0);
  transition: clip-path 1000ms cubic-bezier(0.22, 1, 0.36, 1);
}
.reveal-clip[data-visible='true'] {
  clip-path: inset(0 0 0 0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal,
  .reveal-fade,
  .reveal-clip {
    opacity: 1;
    transform: none;
    clip-path: none;
    transition: none;
  }
  html {
    scroll-behavior: auto;
  }
}

/* ---------- Hero ---------- */
@keyframes hero-zoom {
  from {
    transform: scale(1.1);
  }
  to {
    transform: scale(1);
  }
}
.hero-zoom {
  animation: hero-zoom 2000ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes line-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
.line-grow {
  transform-origin: left;
  animation: line-grow 1100ms cubic-bezier(0.22, 1, 0.36, 1) 300ms forwards;
  transform: scaleX(0);
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.rise {
  opacity: 0;
  animation: rise 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes marquee-scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}
.marquee-track {
  display: flex;
  width: max-content;
  animation: marquee-scroll 38s linear infinite;
}
.marquee-track-slow {
  animation-duration: 58s;
}
.marquee-wrap:hover .marquee-track {
  animation-play-state: paused;
}

/* ---------- Buttons ---------- */
.btn-shine {
  position: relative;
  overflow: hidden;
  isolation: isolate;
}
.btn-shine::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(
    100deg,
    transparent 20%,
    oklch(var(--background-50) / 0.22) 48%,
    transparent 72%
  );
  transform: translateX(-120%);
  transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
}
.btn-shine:hover::before {
  transform: translateX(120%);
}

/* ---------- Form honeypot (champ masqué, discret) ---------- */
.form-alt-field {
  position: absolute !important;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

/* ---------- Card hover sweep ---------- */
.card-img-zoom img {
  transition: transform 800ms cubic-bezier(0.22, 1, 0.36, 1);
}
.group:hover .card-img-zoom img {
  transform: scale(1.06);
}

/* ---------- Repère carré de charte ---------- */
.tick-square {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  background-color: oklch(var(--accent-500));
}

/* ---------- Outline display text ---------- */
.text-outline {
  color: transparent;
  -webkit-text-stroke: 1px oklch(var(--background-50) / 0.26);
}

/* ---------- Repère de section (numérotation technique) ---------- */
.section-index {
  font-family: var(--font-label);
  font-size: 0.6875rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}